import { CSSProperties } from "react";
import { DragOverlay } from "@dnd-kit/core";
import JourneyCard from "./JourneyCard";
import SidebarAutomationCard from "./SidebarAutomationCard";
import { JourneyItemCard } from "./SortableJourneyItem";
import { ActiveDrag, Journey, JourneyItem } from "./journeyPlanner.types";
import { useJourneyPlanner } from "./JourneyPlannerContext";
import {
  getJourneyAlignmentGuides,
  getJourneyPlannerCanvasSize,
  getJourneyPlannerScaledDelta,
} from "./journeyPlannerHelpers";

function JourneyPlannerCanvas({
  activeDrag,
}: {
  activeDrag: ActiveDrag | null;
}) {
  const {
    defaultJourneyName,
    planner,
    journeyRefs,
    zoomLevel,
  } = useJourneyPlanner();

  const activeSidebarAutomation =
    activeDrag?.type === "sidebar-automation"
      ? activeDrag.automation ||
        planner.Automations.find(
          (automation) => automation.Id === activeDrag.automationId
        )
      : null;
  const activeJourney =
    activeDrag?.type === "journey-card"
      ? planner.Journeys.find(
          (journey) => journey.Id === activeDrag.journeyId
        )
      : null;
  const activeJourneyPosition =
    activeJourney && activeDrag
      ? getActiveJourneyPosition(activeJourney, activeDrag, zoomLevel)
      : null;
  const canvasSize = getJourneyPlannerCanvasSize(
    planner.Journeys,
    journeyRefs,
    activeJourneyPosition && activeJourney
      ? { ...activeJourneyPosition, journeyId: activeJourney.Id }
      : null
  );
  const alignmentGuides =
    activeJourneyPosition && activeJourney
      ? getJourneyAlignmentGuides(
          planner.Journeys,
          activeJourney.Id,
          activeJourneyPosition
        )
      : {};

  return (
    <div className="d-flex flex-column flex-grow-1 position-relative surface-grid rounded">
      <div className="position-relative flex-grow-1 overflow-scroll surface-grid-scroll-area">
        <div
          className="position-relative surface-grid-zoom-stage"
          style={{
            "--journey-planner-zoom": zoomLevel,
            "--surface-grid-line-width": `${1 / zoomLevel}px`,
            height: `${Math.ceil(canvasSize.height * zoomLevel)}px`,
            width: `${Math.ceil(canvasSize.width * zoomLevel)}px`,
          } as CSSProperties}
        >
          <div
            className="position-relative surface-grid-zoom-layer"
            style={{
              height: `${canvasSize.height}px`,
              width: `${canvasSize.width}px`,
            }}
          >
            {alignmentGuides.x !== undefined ? (
              <div
                className="surface-grid-alignment-guide surface-grid-alignment-guide-vertical"
                style={{ left: `${alignmentGuides.x}px` }}
              />
            ) : null}
            {alignmentGuides.y !== undefined ? (
              <div
                className="surface-grid-alignment-guide surface-grid-alignment-guide-horizontal"
                style={{ top: `${alignmentGuides.y}px` }}
              />
            ) : null}
            {planner.Journeys.map((journey) => (
              <JourneyCard
                key={journey.Id}
                defaultJourneyName={defaultJourneyName}
                journey={journey}
                setJourneyRef={(node) => {
                  journeyRefs.current[journey.Id] = node;
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <DragOverlay zIndex={2000}>
        {activeSidebarAutomation ? (
          <SidebarAutomationCard
            automation={activeSidebarAutomation}
            className="mb-0"
            draggable={false}
          />
        ) : activeDrag?.type === "journey-item" ? (
          <div className="d-flex align-items-start gap-2">
            <div className="flex-grow-1">
              <JourneyItemCard
                index={activeDrag.index ?? 0}
                item={activeDrag.item as JourneyItem}
              />
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </div>
  );
}

function getActiveJourneyPosition(
  activeJourney: Journey,
  activeDrag: ActiveDrag,
  zoomLevel: number
) {
  const scaledDelta = getJourneyPlannerScaledDelta(
    activeDrag.delta,
    zoomLevel
  );

  return {
    x: Math.max(0, Math.round((activeJourney.X ?? 0) + scaledDelta.x)),
    y: Math.max(0, Math.round((activeJourney.Y ?? 0) + scaledDelta.y)),
  };
}

export default JourneyPlannerCanvas;
